import moment from 'moment/moment';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { BQLink } from '../../../../atoms/bqlink';
import Loader from '../../../../atoms/icons/loader';
import { ArrowRightIcon } from '../../../../atoms/material-icons';
import EmptyState from '../empty-state';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import { isWithinTradingHours, widgetLink } from '../../../../../helpers/utils';
import './most-active-index-options.m.css';

const MostActiveIndexOptions = ({ widget, widgetType, external = false }) => {
  const widgetParam = widget ? '?view=apps' : '';
  const [activeIndexData, setActiveIndexData] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('call');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const queryKey = widgetType === 'index' ? 'getActiveIndexData' : 'getActiveStockData';
  const tradingHolidays = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'trading_holidays'], []),
  );
  const specialTradingDays = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'special_trading_days'], {}),
  );

  const getActiveIndexData = async (type = 'call') => {
    const response = await fetch(
      `/foapi/option/getByVolume?type=${type === 'call' ? '1' : '2'}&instrument=${widgetType === 'index' ? '1' : '2'}&limit=8`,
    );
    return response.json();
  };

  const resultActiveIndexData = useQuery(queryKey, () => getActiveIndexData(selectedFilter), {
    refetchInterval: 30000,
    refetchIntervalInBackground: true,
    onSuccess: (data) => {
      setActiveIndexData(Array.isArray(data?.data) ? data.data : []);
    },
    enabled:
      initialLoading ||
      (!!selectedFilter && isWithinTradingHours(tradingHolidays, specialTradingDays)),
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    setActiveIndexData(
      Array.isArray(resultActiveIndexData?.data?.data) ? resultActiveIndexData.data.data : [],
    );
  }, [resultActiveIndexData?.isSuccess, resultActiveIndexData?.data?.data]);

  const clickHandler = async (type) => {
    setLoading(true);
    setSelectedFilter(type);
    const data = await getActiveIndexData(type);
    setActiveIndexData(data?.data);
    setLoading(false);
  };

  return (
    <React.Fragment>
      {initialLoading ? (
        <div styleName="initial-loading-container"></div>
      ) : (
        <div styleName={`most-active-index-container ${external ? 'external' : ''}`}>
          <div styleName="header-strip">
            <span styleName="title">
              MOST ACTIVE {widgetType === 'index' ? 'INDEX' : 'STOCK'} OPTIONS
            </span>
            <div styleName="filter-btn-container">
              <button
                styleName={`filter-btn ${selectedFilter === 'call' ? 'filter-selected' : ''}`}
                onClick={() => clickHandler('call')}
              >
                Calls
              </button>
              <button
                styleName={`filter-btn ${selectedFilter === 'put' ? 'filter-selected' : ''}`}
                onClick={() => clickHandler('put')}
              >
                Puts
              </button>
            </div>
          </div>

          {activeIndexData && activeIndexData?.length !== 0 ? (
            <>
              {loading ? (
                <div styleName="loading-container-ext">
                  <div styleName="loading-container-int">
                    <Loader color={'#dadee1'} />
                  </div>
                </div>
              ) : (
                <div styleName="content-container">
                  <div styleName="table-body-container">
                    <table>
                      <thead styleName="table-header">
                        <tr>
                          <th styleName="column-title">Symbol Strike</th>
                          <th styleName="column-title-alt">Expiry</th>
                          <th styleName="column-title-alt">Premium</th>
                          <th styleName="column-title-alt">Spot price</th>
                          <th styleName="column-title-alt">
                            Volume
                            <br />
                            In Contracts
                          </th>
                          <th styleName="column-title-alt">
                            Open Interest
                            <br /> (In Contracts)
                          </th>
                          <th styleName="column-title-alt">
                            OI Turnover
                            <br /> (Rs Cr)
                          </th>
                          <th styleName="column-title-alt">
                            Notional Turnover
                            <br /> (Rs Cr)
                          </th>
                          <th styleName="column-title-alt">
                            Premium Turnover
                            <br /> (Rs Cr)
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {activeIndexData &&
                          activeIndexData?.slice(0, 8)?.map((dataPoint, index) => (
                            <tr styleName="data-row" key={index}>
                              <td>
                                {dataPoint?.['symbol-strike'] ? (
                                  <span styleName="name">{dataPoint?.['symbol-strike']}</span>
                                ) : (
                                  <span styleName="name">-</span>
                                )}
                              </td>
                              <td styleName="column-value">
                                {moment(dataPoint['expiry-date']).format('DD MMM')}
                              </td>
                              <td styleName="column-value">
                                {dataPoint?.premium ? dataPoint?.premium : '-'}
                              </td>
                              <td styleName="column-value">
                                {dataPoint?.['spot-price'] ? (
                                  <span>{dataPoint?.['spot-price']}</span>
                                ) : (
                                  <span>0.00</span>
                                )}
                              </td>
                              <td styleName="column-value">
                                {dataPoint['volumes-in-contracts'] ? (
                                  <span>{dataPoint?.['volumes-in-contracts']}</span>
                                ) : (
                                  <span>0.00</span>
                                )}
                              </td>
                              <td styleName="column-value">
                                {dataPoint?.['open-interest-in-contracts'] ? (
                                  <span>{dataPoint?.['open-interest-in-contracts']}</span>
                                ) : (
                                  <span>0.00</span>
                                )}
                              </td>
                              <td styleName="column-value">
                                {dataPoint?.['open-interest-turnover']
                                  ? (dataPoint?.['open-interest-turnover'] / 10000000)?.toFixed(2)
                                  : '0.00'}
                              </td>
                              <td styleName="column-value">
                                {dataPoint?.['notional-turnover'] ? (
                                  <span>
                                    {(dataPoint?.['notional-turnover'] / 10000000)?.toFixed(2)}
                                  </span>
                                ) : (
                                  <span>0.00</span>
                                )}
                              </td>
                              <td styleName="column-value">
                                {dataPoint?.['premium-turnover']
                                  ? (dataPoint?.['premium-turnover'] / 10000000)?.toFixed(2)
                                  : '0.00'}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div styleName="no-data-container-ext">
              <div styleName="no-data-container-int">
                <EmptyState />
              </div>
            </div>
          )}

          {!external && (
            <div styleName="view-btn-container">
              <BQLink
                styleName="view-more-btn"
                href={
                  widgetType === 'index'
                    ? widgetLink(
                        `/markets/option-chain/most-active-index-options${widgetParam}`,
                        widget,
                      )
                    : widgetLink(
                        `/markets/option-chain/most-active-stock-options${widgetParam}`,
                        widget,
                      )
                }
                target="_blank"
                rel="noreferrer"
              >
                View All Options
                <ArrowRightIcon styleName="tab-icon" color={'#2b2b2b'} />
              </BQLink>
            </div>
          )}
        </div>
      )}
    </React.Fragment>
  );
};

MostActiveIndexOptions.propTypes = {
  widgetType: PropTypes.string,
};

MostActiveIndexOptions.defaultProps = {
  widgetType: 'index',
  widet: false,
};

export default MostActiveIndexOptions;
